<template>
  <v-container fluid class="pa-4">
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="success white--text">
            <v-icon left color="white">mdi-factory</v-icon>
            Proizvodnja dan (po normativu)
          </v-card-title>

          <v-stepper v-model="step" vertical>
            <!-- Step 1: Odabir proizvoda -->
            <v-stepper-step :complete="step > 1" step="1" editable>
              Odabir proizvoda
              <small>Odaberite proizvod i količinu proizvodnje</small>
            </v-stepper-step>
            <v-stepper-content step="1">
              <product-selector
                v-model="selectedProduct"
                @warehouse-selected="outputWarehouse = $event"
                @quantity-changed="desiredQuantity = $event"
              />
              <v-btn
                color="primary"
                @click="loadNormative"
                :disabled="!canProceedStep1"
              >
                Učitaj normativ
              </v-btn>
            </v-stepper-content>

            <!-- Step 2: Odabir sastojaka -->
            <v-stepper-step :complete="step > 2" step="2" editable>
              Sastojci i sirovine
              <small>Provjerite normativ i odaberite skladišta</small>
            </v-stepper-step>
            <v-stepper-content step="2">
              <ingredient-selector
                v-model="ingredients"
                :product="selectedProduct"
                :desired-quantity="desiredQuantity"
                :warehouses="warehouses"
              />
              <v-btn text @click="step = 1" class="mr-2">Nazad</v-btn>
              <v-btn
                color="primary"
                @click="validateStep2"
                :disabled="!canProceedStep2"
              >
                Nastavi
              </v-btn>
            </v-stepper-content>

            <!-- Step 3: Pregled i potvrda -->
            <v-stepper-step step="3">
              Pregled i potvrda
              <small>Pregledajte i potvrdite proizvodnju</small>
            </v-stepper-step>
            <v-stepper-content step="3">
              <production-confirmation
                :product="selectedProduct"
                :output-warehouse="outputWarehouse"
                :desired-quantity="desiredQuantity"
                :ingredients="ingredients"
              />
              <v-btn text @click="step = 2" class="mr-2">Nazad</v-btn>
              <v-btn
                color="success"
                @click="executeProduction"
                :loading="loading"
              >
                <v-icon left>mdi-check</v-icon>
                Izvrši proizvodnju
              </v-btn>
            </v-stepper-content>
          </v-stepper>
        </v-card>
      </v-col>
    </v-row>

    <!-- Success dialog -->
    <v-dialog v-model="successDialog" max-width="500">
      <v-card v-if="selectedProduct">
        <v-card-title class="success white--text">
          <v-icon left color="white">mdi-check-circle</v-icon>
          Proizvodnja uspješna
        </v-card-title>
        <v-card-text class="pt-4">
          <p>Proizvodnja je uspješno izvršena!</p>
          <p class="mb-0">
            Proizvedeno: <strong>{{ desiredQuantity }} kg</strong> od
            <strong>{{ selectedProduct.name }}</strong>
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="success" text @click="resetProduction">
            Nova proizvodnja
          </v-btn>
          <v-btn color="primary" text @click="successDialog = false">
            Zatvori
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import ProductSelector from './normative-production/ProductSelector.vue'
import IngredientSelector from './normative-production/IngredientSelector.vue'
import ProductionConfirmation from './normative-production/ProductionConfirmation.vue'

export default {
  name: 'NormativeProduction',
  components: {
    ProductSelector,
    IngredientSelector,
    ProductionConfirmation
  },
  data () {
    return {
      step: 1,
      loading: false,
      successDialog: false,

      // Step 1 data
      selectedProduct: null,
      outputWarehouse: null,
      desiredQuantity: 0,

      // Step 2 data
      ingredients: [],

      // Mock warehouses
      warehouses: [
        { id: 1, name: 'Glavno skladište', location: 'Zagreb' },
        { id: 2, name: 'Skladište sirovine', location: 'Zagreb' },
        { id: 3, name: 'Skladište gotovih proizvoda', location: 'Split' },
        { id: 4, name: 'Hladnjača', location: 'Rijeka' }
      ],

      // Mock products with normatives
      productsWithNormatives: [
        {
          id: 101,
          name: 'Ćevapčići',
          code: 'CEV-001',
          unit: 'kg',
          normative: [
            { itemId: 1, name: 'Junetina sirovina', quantity: 0.5, unit: 'kg' },
            { itemId: 3, name: 'Svinjetina sirovina', quantity: 0.5, unit: 'kg' },
            { itemId: 4, name: 'Mirodije mix', quantity: 0.1, unit: 'kg' }
          ]
        },
        {
          id: 102,
          name: 'Pljeskavice',
          code: 'PLJ-001',
          unit: 'kg',
          normative: [
            { itemId: 1, name: 'Junetina sirovina', quantity: 0.7, unit: 'kg' },
            { itemId: 3, name: 'Svinjetina sirovina', quantity: 0.3, unit: 'kg' },
            { itemId: 5, name: 'Sol', quantity: 0.02, unit: 'kg' },
            { itemId: 6, name: 'Papar crni', quantity: 0.01, unit: 'kg' }
          ]
        },
        {
          id: 103,
          name: 'Hamburger',
          code: 'HAM-001',
          unit: 'kg',
          normative: [
            { itemId: 1, name: 'Junetina sirovina', quantity: 0.8, unit: 'kg' },
            { itemId: 7, name: 'Luk suh', quantity: 0.15, unit: 'kg' },
            { itemId: 8, name: 'Češnjak', quantity: 0.05, unit: 'kg' },
            { itemId: 5, name: 'Sol', quantity: 0.02, unit: 'kg' }
          ]
        }
      ],

      // Mock available items on warehouses
      availableItems: [
        { id: 1, name: 'Junetina sirovina', code: 'JUN-001', stock: 150, unit: 'kg', pricePerKg: 12.50 },
        { id: 2, name: 'Soja sirovina', code: 'SOJ-001', stock: 200, unit: 'kg', pricePerKg: 3.20 },
        { id: 3, name: 'Svinjetina sirovina', code: 'SVI-001', stock: 100, unit: 'kg', pricePerKg: 8.90 },
        { id: 4, name: 'Mirodije mix', code: 'MIR-001', stock: 50, unit: 'kg', pricePerKg: 15.00 },
        { id: 5, name: 'Sol', code: 'SOL-001', stock: 80, unit: 'kg', pricePerKg: 0.50 },
        { id: 6, name: 'Papar crni', code: 'PAP-001', stock: 20, unit: 'kg', pricePerKg: 25.00 },
        { id: 7, name: 'Luk suh', code: 'LUK-001', stock: 60, unit: 'kg', pricePerKg: 1.80 },
        { id: 8, name: 'Češnjak', code: 'CES-001', stock: 30, unit: 'kg', pricePerKg: 8.50 }
      ]
    }
  },
  computed: {
    canProceedStep1 () {
      return this.selectedProduct && this.outputWarehouse && this.desiredQuantity > 0
    },
    canProceedStep2 () {
      return this.ingredients.length > 0 &&
             this.ingredients.every(ing =>
               ing.warehouse &&
               ing.quantity > 0 &&
               ing.quantity <= ing.availableStock
             )
    }
  },
  methods: {
    loadNormative () {
      if (!this.canProceedStep1) return

      // Load normative for selected product
      const product = this.productsWithNormatives.find(p => p.id === this.selectedProduct.id)

      if (product && product.normative) {
        this.ingredients = product.normative.map(normItem => {
          const availableItem = this.availableItems.find(item => item.id === normItem.itemId)

          return {
            ...normItem,
            itemId: normItem.itemId,
            name: normItem.name,
            quantityPerUnit: normItem.quantity, // Količina po 1kg proizvoda
            quantity: normItem.quantity * this.desiredQuantity, // Ukupno potrebno
            unit: normItem.unit,
            warehouse: null, // Korisnik će odabrati
            availableStock: availableItem ? availableItem.stock : 0,
            pricePerKg: availableItem ? availableItem.pricePerKg : 0,
            fromNormative: true
          }
        })

        this.step = 2
      }
    },
    validateStep2 () {
      if (this.canProceedStep2) {
        this.step = 3
      }
    },
    executeProduction () {
      this.loading = true

      // Simulate API call
      setTimeout(() => {
        console.log('Executing normative production:', {
          product: this.selectedProduct,
          outputWarehouse: this.outputWarehouse,
          desiredQuantity: this.desiredQuantity,
          ingredients: this.ingredients
        })

        this.loading = false
        this.successDialog = true
      }, 1500)
    },
    resetProduction () {
      this.successDialog = false
      this.step = 1
      this.selectedProduct = null
      this.outputWarehouse = null
      this.desiredQuantity = 0
      this.ingredients = []
    }
  }
}
</script>

<style scoped>
.v-stepper {
  box-shadow: none;
}
</style>
